// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * NodeIcon - Bold outline icon for pipeline nodes.
 * No background - clean icon with consistent stroke width.
 */

import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/components/ui'
import { NODE_ICON_STYLE, type NodeStatus } from '../../types'

export interface NodeIconProps {
  icon: ReactNode
  status?: NodeStatus
}

const STATUS_COLORS = {
  idle: 'text-slate-700',
  running: 'text-blue-600',
  success: 'text-emerald-600',
  error: 'text-red-600',
} as const

export const NodeIcon: React.FC<NodeIconProps> = ({ icon, status = 'idle' }) => (
  <div
    className={cn(
      'flex-shrink-0',
      NODE_ICON_STYLE.size,
      STATUS_COLORS[status],
      '[&>svg]:w-full [&>svg]:h-full [&>svg]:stroke-2',
    )}
  >
    {status === 'running' ? <Loader2 className="animate-spin" /> : icon}
  </div>
)

NodeIcon.displayName = 'NodeIcon'
